import {
  type MeetingListFilter,
  type MeetingListPageState,
  useStores,
} from '@littlebird/sdk';
import { useCallback, useEffect, useState } from 'react';

import { logError } from '@/lib/errorLogging';

export function useMeetingListPagination(filter: MeetingListFilter) {
  const { meetings } = useStores();
  const [queryState, setQueryState] = useState<{
    pageState?: MeetingListPageState;
    hasMore: boolean;
    isLoaded: boolean;
  }>({ hasMore: false, isLoaded: false });

  useEffect(() => {
    let cancelled = false;
    setQueryState({ hasMore: false, isLoaded: false });

    void meetings
      .queryInitialListPage(filter)
      .then((page) => {
        if (cancelled) return;
        setQueryState({
          pageState: page.state,
          hasMore: page.hasMore,
          isLoaded: true,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setQueryState({ hasMore: false, isLoaded: true });
        logError(err, 'Failed to load filtered meetings', {
          context: 'useMeetingListPagination',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [filter, meetings]);

  const loadNextPage = useCallback(async () => {
    const pageState = queryState.pageState;
    if (!pageState || !queryState.hasMore) return;

    try {
      const page = await meetings.queryNextListPage(filter, pageState);
      setQueryState((current) => {
        if (current.pageState !== pageState) return current;
        return {
          pageState: page.state,
          hasMore: page.hasMore,
          isLoaded: true,
        };
      });
    } catch (err) {
      logError(err, 'Failed to load more filtered meetings', {
        context: 'useMeetingListPagination',
      });
    }
  }, [filter, queryState.hasMore, queryState.pageState, meetings]);

  return { ...queryState, loadNextPage };
}
